import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, ShieldAlert, ShieldCheck, AlertTriangle, ChevronDown, ChevronRight, RefreshCw, Activity } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

const ANTI_PATTERN_ICONS = {
  'Noise Dumping': '🗑️',
  'Signal Flooding': '🌊',
  'Permanent Bridges': '🌉',
  'Resolution Without Ownership': '👻',
  'Upward Blockage': '🚫',
  'Capability Theater': '🎭'
};

const healthColors = {
  healthy: { bg: 'bg-emerald-500/20', text: 'text-emerald-300', border: 'border-emerald-500/20', icon: ShieldCheck },
  caution: { bg: 'bg-amber-500/20', text: 'text-amber-300', border: 'border-amber-500/20', icon: AlertTriangle },
  critical: { bg: 'bg-red-500/20', text: 'text-red-300', border: 'border-red-500/20', icon: ShieldAlert }
};

const statusColors = {
  detected: { bg: 'bg-red-500/20', text: 'text-red-300', icon: ShieldAlert },
  warning: { bg: 'bg-amber-500/20', text: 'text-amber-300', icon: AlertTriangle },
  clear: { bg: 'bg-emerald-500/20', text: 'text-emerald-300', icon: ShieldCheck }
};

export default function AntiPatternDetector() {
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null);
  const [expandedPattern, setExpandedPattern] = useState(null);

  const handleScan = async () => {
    setScanning(true);
    try {
      const response = await base44.functions.invoke('councilAntiPatternScan', {});
      const data = response.data || response;
      setResult(data);
    } catch (err) {
      console.error('Anti-pattern scan failed:', err);
    } finally {
      setScanning(false);
    }
  };

  if (scanning) {
    return (
      <Card className="glass-panel border-indigo-500/20">
        <CardContent className="py-6 text-center">
          <Loader2 className="w-6 h-6 text-indigo-400 animate-spin mx-auto mb-2" />
          <p className="text-sm text-indigo-300">Scanning governance patterns...</p>
        </CardContent>
      </Card>);

  }

  if (!result) {
    return (
      <Card className="glass-panel border-indigo-500/20">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-indigo-400 flex items-center gap-2">
            <Activity className="w-4 h-4" /> Governance Anti-Pattern Detector
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-slate-400 mb-3">
            AI scans the full governance landscape for 6 systemic dysfunction patterns: noise dumping, signal flooding, permanent bridges, unowned resolutions, upward blockage, and capability theater.
          </p>
          <Button
            onClick={handleScan}
            className="w-full bg-indigo-600 hover:bg-indigo-700 text-white h-9 text-sm">

            <Activity className="w-4 h-4 mr-2" /> Run Governance Health Scan
          </Button>
        </CardContent>
      </Card>);

  }

  const health = healthColors[result.overall_health] || healthColors.caution;
  const HealthIcon = health.icon;
  const antiPatterns = Array.isArray(result.anti_patterns) ? result.anti_patterns : [];
  const detectedCount = antiPatterns.filter((p) => p.status === 'detected').length;
  const warningCount = antiPatterns.filter((p) => p.status === 'warning').length;

  return (
    <Card className={`glass-panel ${health.border}`}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm text-indigo-500 flex items-center gap-2">
            <Activity className="w-4 h-4" /> Governance Health
          </CardTitle>
          <div className="flex items-center gap-2">
            <Badge className={`${health.bg} ${health.text}`}>
              <HealthIcon className="w-3 h-3 mr-1" />
              {result.overall_health?.toUpperCase()}
            </Badge>
            {detectedCount > 0 &&
            <Badge className="bg-red-500/20 text-red-500 text-[10px]">
                {detectedCount} detected
              </Badge>
            }
            {warningCount > 0 &&
            <Badge className="bg-amber-500/20 text-amber-500 text-[10px]">
                {warningCount} warning{warningCount !== 1 ? 's' : ''}
              </Badge>
            }
            <Button
              size="sm" variant="ghost"
              onClick={handleScan}
              className="h-7 text-xs text-indigo-500 hover:text-indigo-500">

              <RefreshCw className="w-3 h-3 mr-1" /> Re-scan
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Overall Summary */}
        {result.overall_summary &&
        <p className="text-slate-600 text-xs">{result.overall_summary}</p>
        }

        {/* Anti-Pattern List */}
        {antiPatterns.map((pattern, idx) => {
          const status = statusColors[pattern.status] || statusColors.warning;
          const StatusIcon = status.icon;
          const icon = Object.entries(ANTI_PATTERN_ICONS).find(([key]) =>
          pattern.name?.toLowerCase().includes(key.toLowerCase())
          )?.[1] || '⚠️';
          const isExpanded = expandedPattern === idx;

          return (
            <Collapsible
              key={idx}
              open={isExpanded}
              onOpenChange={() => setExpandedPattern(isExpanded ? null : idx)}>

              <CollapsibleTrigger className="w-full">
                <div className="bg-zinc-50 text-slate-500 p-2.5 rounded-lg flex items-center gap-2 border transition-colors cursor-pointer hover:bg-slate-800/30 border-slate-700">






                  {isExpanded ?
                  <ChevronDown className={`w-4 h-4 ${status.text}`} /> :
                  <ChevronRight className={`w-4 h-4 ${status.text}`} />
                  }
                  <span className="text-base">{icon}</span>
                  <span className="text-slate-500 text-sm text-left flex-1">{pattern.name}</span>
                  <Badge className={`${status.bg} ${status.text} text-[10px]`}>
                    <StatusIcon className="w-3 h-3 mr-1" />
                    {pattern.status}
                  </Badge>
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="bg-zinc-50 mt-1 mr-2 ml-8 p-3 rounded-lg space-y-2 border border-slate-700/50">
                  {/* Evidence */}
                  <div>
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-0.5">Evidence</p>
                    <p className="text-slate-600 text-xs">{pattern.evidence}</p>
                  </div>

                  {/* Corrective Action */}
                  <div>
                    <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-0.5">Corrective Action</p>
                    <p className="text-slate-600 text-xs">{pattern.corrective_action}</p>
                  </div>

                  {/* Affected Entities */}
                  {Array.isArray(pattern.affected_entity_ids) && pattern.affected_entity_ids.length > 0 &&
                  <div className="flex flex-wrap gap-1">
                      <span className="text-[10px] text-slate-500">Affected:</span>
                      {pattern.affected_entity_ids.map((id) =>
                    <Badge key={id} className="bg-slate-700 text-slate-500 text-[10px]">#{id}</Badge>
                    )}
                    </div>
                  }
                </div>
              </CollapsibleContent>
            </Collapsible>);

        })}

        {/* Scan timestamp */}
        {result.scanned_at &&
        <p className="text-[10px] text-slate-600 text-right">
            Scanned: {new Date(result.scanned_at).toLocaleString()}
          </p>
        }
      </CardContent>
    </Card>);

}